import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Plus,
  Star,
  Trash2,
  Edit,
  ChevronDown,
  ChevronRight,
  Layers,
  CheckSquare,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { useTRPC } from "@/trpc";
import { useOrganization } from "@/hooks/use-organization";
import { queryClient } from "@/ajax/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  HiringProcessTemplateForm,
  HiringProcessTemplateFormValues,
  HiringProcessTemplateSubmitPayload,
  ExistingAttachmentsMap,
} from "./template-form";
import { toast } from "sonner";
import { trpcClient } from "@/trpc";
import { tusUploadFile } from "@/ajax/upload/tusUploadFile";
import {
  HiringProcessAssigneeRole,
  HiringProcessItemType,
} from "@api/shared-runtime/enums";
import type { RouterOutputs } from "@/trpc";

type HiringProcessTemplateWithStages = RouterOutputs["hiringProcess"]["get"];
type HiringProcessTemplate = RouterOutputs["hiringProcess"]["list"][number];

const scanTerminalStatuses = new Set(["clean", "quarantined"]);

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function TemplateRow({
  template,
  organizationBusinessUnitId,
  onEdit,
  onDelete,
}: {
  template: HiringProcessTemplate;
  organizationBusinessUnitId: string;
  onEdit: (template: HiringProcessTemplate) => void;
  onDelete: (template: HiringProcessTemplate) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const trpc = useTRPC();

  const { data: templateDetails } = useQuery(
    trpc.hiringProcess.get.queryOptions(
      {
        organizationBusinessUnitId,
        id: template.id,
      },
      { enabled: isExpanded },
    ),
  );

  const stageCount = template.stages?.length ?? 0;

  return (
    <div className="border border-gray-100 rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-white hover:bg-gray-50 transition-colors">
        <button
          onClick={() => setIsExpanded((prev) => !prev)}
          className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 text-left"
          aria-label={isExpanded ? "Collapse" : "Expand"}
        >
          <span className="text-gray-400 hover:text-gray-600 shrink-0">
            {isExpanded ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronRight size={16} />
            )}
          </span>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-gray-900 text-sm sm:text-base">
                {template.name}
              </span>
              {template.isDefault && (
                <Badge
                  variant="outline"
                  className="bg-amber-50 text-amber-700 border-amber-200 text-xs"
                >
                  <Star size={10} className="mr-1" />
                  Default
                </Badge>
              )}
              <Badge
                variant="outline"
                className="bg-blue-50 text-blue-700 border-blue-200 text-xs"
              >
                <Layers size={10} className="mr-1" />
                {stageCount} stage{stageCount !== 1 ? "s" : ""}
              </Badge>
            </div>
            {template.description && (
              <p className="text-sm text-gray-500 mt-0.5 truncate">
                {template.description}
              </p>
            )}
          </div>
        </button>

        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8 px-2"
            onClick={() => onEdit(template)}
          >
            <Edit size={14} className="sm:mr-1" />
            <span className="hidden sm:inline">Edit</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8 px-2"
            onClick={() => onDelete(template)}
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
          {!templateDetails ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-8 bg-gray-200 rounded animate-pulse"
                />
              ))}
            </div>
          ) : templateDetails.stages.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No stages defined.</p>
          ) : (
            <div className="space-y-4">
              {templateDetails.stages.map((stage, stageIdx) => (
                <div key={stage.id}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Stage {stageIdx + 1}: {stage.name}
                    </span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>

                  {stage.items.length === 0 ? (
                    <p className="text-xs text-gray-400 italic pl-2">
                      No items in this stage.
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {stage.items.map((item, itemIdx) => (
                        <div
                          key={item.id}
                          className="flex items-start gap-2 sm:gap-3 text-sm py-1.5 border-b border-gray-100 last:border-0"
                        >
                          <span className="text-gray-400 text-xs font-mono mt-0.5 w-5 text-right shrink-0">
                            {itemIdx + 1}.
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-gray-800">
                                {item.title}
                              </span>
                              {!item.isRequired && (
                                <span className="text-xs text-gray-400">
                                  (optional)
                                </span>
                              )}
                              {item.itemType !== HiringProcessItemType.Task && (
                                <Badge
                                  variant="outline"
                                  className="text-xs h-4 px-1.5 bg-purple-50 text-purple-700 border-purple-200"
                                >
                                  {item.itemType === HiringProcessItemType.Form
                                    ? "Form"
                                    : item.itemType ===
                                        HiringProcessItemType.DocumentShare
                                      ? "Shared"
                                      : item.itemType ===
                                          HiringProcessItemType.DocumentSigning
                                        ? "PDF"
                                        : "Upload"}
                                </Badge>
                              )}
                            </div>
                            {item.description && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                {item.description}
                              </p>
                            )}
                          </div>
                          <Badge
                            variant="outline"
                            className={
                              item.assigneeRole ===
                              HiringProcessAssigneeRole.Provider
                                ? "bg-purple-50 text-purple-700 border-purple-200 text-xs shrink-0"
                                : "bg-green-50 text-green-700 border-green-200 text-xs shrink-0"
                            }
                          >
                            {item.assigneeRole ===
                            HiringProcessAssigneeRole.Provider
                              ? "Provider"
                              : "Org"}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TemplateDeleteDialog({
  template,
  organizationBusinessUnitId,
  onOpenChange,
  onConfirm,
}: {
  template: HiringProcessTemplate | null;
  organizationBusinessUnitId: string;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  const trpc = useTRPC();
  const { data: usage, isLoading } = useQuery(
    trpc.hiringProcess.getUsage.queryOptions(
      {
        organizationBusinessUnitId,
        id: template?.id ?? "",
      },
      { enabled: !!template },
    ),
  );

  const isInUse = (usage?.jobPosts.length ?? 0) > 0;

  return (
    <Dialog open={!!template} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] sm:max-h-[90vh] w-[95vw] sm:w-full">
        <DialogHeader>
          <DialogTitle>Delete Template</DialogTitle>
          {isLoading ? (
            <DialogDescription>Checking usage...</DialogDescription>
          ) : isInUse ? (
            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">This template is in use</p>
                  <p className="mt-1">
                    Remove it from the following job{" "}
                    {usage!.jobPosts.length > 1 ? "posts" : "post"} before
                    deleting:
                  </p>
                  <ul className="mt-1.5 list-disc pl-4 space-y-0.5">
                    {usage!.jobPosts.map((jp) => (
                      <li key={jp.id} className="font-medium">
                        {jp.title || "Untitled job post"}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <DialogDescription>
              Are you sure you want to delete &ldquo;{template?.name}&rdquo;?
              This action cannot be undone.
            </DialogDescription>
          )}
        </DialogHeader>
        <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto"
          >
            {isInUse ? "Close" : "Cancel"}
          </Button>
          {!isLoading && !isInUse && (
            <Button
              variant="destructive"
              onClick={onConfirm}
              className="w-full sm:w-auto"
            >
              Delete
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function HiringProcessTemplatesPage() {
  const trpc = useTRPC();
  const { selectedOrgBusinessUnit } = useOrganization();
  const buId = selectedOrgBusinessUnit.id;

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<HiringProcessTemplate | null>(null);
  const [editingTemplateDetails, setEditingTemplateDetails] =
    useState<HiringProcessTemplateWithStages | null>(null);
  const [deletingTemplate, setDeletingTemplate] =
    useState<HiringProcessTemplate | null>(null);

  const { data: templates, isLoading } = useQuery(
    trpc.hiringProcess.list.queryOptions({
      organizationBusinessUnitId: buId,
    }),
  );

  const createMutation = useMutation(
    trpc.hiringProcess.create.mutationOptions(),
  );

  const updateMutation = useMutation(
    trpc.hiringProcess.update.mutationOptions(),
  );

  const deleteMutation = useMutation(
    trpc.hiringProcess.delete.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          trpc.hiringProcess.list.queryOptions({
            organizationBusinessUnitId: buId,
          }),
        );
        setDeletingTemplate(null);
        toast.success("Template deleted");
      },
      onError: (err) => {
        toast.error(err.message || "Failed to delete template");
      },
    }),
  );

  /** Upload pending files via TUS and confirm them */
  const uploadAndConfirm = async (
    pendingUploads: {
      fileUploadId: string;
      storagePath: string;
      bucketName: string;
      uploadToken: string;
      stageIndex: number;
      itemIndex: number;
      fileIndex: number;
    }[],
    localFiles: Map<string, File[]>,
    stages: HiringProcessTemplateFormValues["stages"],
  ) => {
    if (pendingUploads.length === 0) {
      return;
    }

    await Promise.all(
      pendingUploads.map(async (upload) => {
        const key = `${upload.stageIndex}-${upload.itemIndex}`;
        const files = localFiles.get(key) ?? [];
        const file = files[upload.fileIndex];
        if (!file) {
          return;
        }

        await tusUploadFile({
          file,
          bucketName: upload.bucketName,
          objectName: upload.storagePath,
          uploadToken: upload.uploadToken,
        });
      }),
    );

    await trpcClient.fileAttachment.confirmUploads.mutate({
      fileUploadIds: pendingUploads.map((u) => u.fileUploadId),
    });

    const fileUploadIds = pendingUploads.map((u) => u.fileUploadId);
    for (let attempt = 0; attempt < 20; attempt++) {
      const statuses = await trpcClient.fileAttachment.getUploadStatuses.query({
        fileUploadIds,
      });
      const rejected = statuses.find((s) => s.scanStatus === "quarantined");
      if (rejected) {
        throw new Error(
          `${rejected.fileName} was removed by the security scan and was not saved to the hiring process.`,
        );
      }
      if (statuses.every((s) => s.scanStatus === "clean")) {
        return;
      }
      if (statuses.every((s) => scanTerminalStatuses.has(s.scanStatus))) {
        break;
      }
      await sleep(1500);
    }

    throw new Error(
      "File security scan is still processing. The hiring process was not saved with that file. Try again after the scan finishes.",
    );
  };

  const mapStagesInput = (stages: HiringProcessTemplateFormValues["stages"]) =>
    stages.map((stage) => ({
      id: stage.id,
      name: stage.name,
      description: stage.description ?? null,
      sortOrder: stage.sortOrder,
      items: stage.items.map((item, idx) => ({
        id: item.id,
        // Existing items get their db id as the ref; new ones get a stable
        // client-generated ref tied to stage+index for predecessor wiring.
        ref: item.ref ?? item.id ?? `new-${stage.sortOrder}-${idx}`,
        predecessors: item.predecessors ?? [],
        title: item.title,
        description: item.description ?? null,
        sortOrder: item.sortOrder,
        isRequired: item.isRequired,
        assigneeRole: item.assigneeRole,
        itemType: item.itemType ?? HiringProcessItemType.Task,
        formId: item.formId ?? null,
        richTextContent: item.richTextContent ?? null,
        fileUploadIds: item.fileUploadIds ?? [],
        newFiles: item.newFiles ?? [],
        links: (item.links ?? []).map((link, linkIdx) => ({
          id: link.id,
          url: link.url,
          label: link.label ?? null,
          sortOrder: link.sortOrder ?? linkIdx,
        })),
        requestedCredentialFileTypeId:
          item.requestedCredentialFileTypeId ?? null,
      })),
    }));

  const handleCreate = async (payload: HiringProcessTemplateSubmitPayload) => {
    const { values, localFiles } = payload;
    let createdTemplateId: string | null = null;
    try {
      const result = await createMutation.mutateAsync({
        organizationBusinessUnitId: buId,
        name: values.name,
        description: values.description ?? null,
        isDefault: values.isDefault,
        stages: mapStagesInput(values.stages),
      });
      createdTemplateId = result.template.id;

      await uploadAndConfirm(result.pendingUploads, localFiles, values.stages);

      await queryClient.invalidateQueries(
        trpc.hiringProcess.list.queryOptions({
          organizationBusinessUnitId: buId,
        }),
      );
      setIsCreateOpen(false);
      toast.success("Hiring process template created");
    } catch (err) {
      if (createdTemplateId) {
        try {
          await trpcClient.hiringProcess.delete.mutate({
            organizationBusinessUnitId: buId,
            id: createdTemplateId,
          });
          await queryClient.invalidateQueries(
            trpc.hiringProcess.list.queryOptions({
              organizationBusinessUnitId: buId,
            }),
          );
        } catch {
          // Preserve the scan/upload error as the actionable message.
        }
      }
      toast.error(
        err instanceof Error ? err.message : "Failed to create template",
      );
    }
  };

  const handleUpdate = async (payload: HiringProcessTemplateSubmitPayload) => {
    if (!editingTemplate) {
      return;
    }
    const { values, localFiles, removedStageIds, removedItemIds } = payload;
    let shouldStripNewFiles = false;
    try {
      const result = await updateMutation.mutateAsync({
        organizationBusinessUnitId: buId,
        id: editingTemplate.id,
        name: values.name,
        description: values.description ?? null,
        isDefault: values.isDefault,
        stages: mapStagesInput(values.stages),
        removedStageIds,
        removedItemIds,
      });
      shouldStripNewFiles = result.pendingUploads.length > 0;

      await uploadAndConfirm(result.pendingUploads, localFiles, values.stages);

      await queryClient.invalidateQueries(
        trpc.hiringProcess.list.queryOptions({
          organizationBusinessUnitId: buId,
        }),
      );
      if (editingTemplate) {
        await queryClient.invalidateQueries(
          trpc.hiringProcess.get.queryOptions({
            organizationBusinessUnitId: buId,
            id: editingTemplate.id,
          }),
        );
      }
      setEditingTemplate(null);
      setEditingTemplateDetails(null);
      toast.success("Hiring process template updated");
    } catch (err) {
      if (shouldStripNewFiles) {
        try {
          await trpcClient.hiringProcess.update.mutate({
            organizationBusinessUnitId: buId,
            id: editingTemplate.id,
            name: values.name,
            description: values.description ?? null,
            isDefault: values.isDefault,
            stages: mapStagesInput(values.stages).map((stage) => ({
              ...stage,
              items: stage.items.map((item) => ({
                ...item,
                newFiles: [],
              })),
            })),
            removedStageIds,
            removedItemIds,
          });
          await queryClient.invalidateQueries(
            trpc.hiringProcess.get.queryOptions({
              organizationBusinessUnitId: buId,
              id: editingTemplate.id,
            }),
          );
        } catch {
          // Preserve the scan/upload error as the actionable message.
        }
      }
      toast.error(
        err instanceof Error ? err.message : "Failed to update template",
      );
    }
  };

  const handleDelete = () => {
    if (!deletingTemplate) {
      return;
    }
    deleteMutation.mutate({
      organizationBusinessUnitId: buId,
      id: deletingTemplate.id,
    });
  };

  const handleEditClick = async (template: HiringProcessTemplate) => {
    setEditingTemplate(template);
    const details = await queryClient.fetchQuery(
      trpc.hiringProcess.get.queryOptions({
        organizationBusinessUnitId: buId,
        id: template.id,
      }),
    );
    setEditingTemplateDetails(details);
  };

  const editDefaultValues:
    | Partial<HiringProcessTemplateFormValues>
    | undefined = editingTemplateDetails
    ? {
        name: editingTemplateDetails.name,
        description: editingTemplateDetails.description ?? "",
        isDefault: editingTemplateDetails.isDefault,
        stages: editingTemplateDetails.stages.map((stage) => ({
          id: stage.id,
          name: stage.name,
          description: stage.description ?? "",
          sortOrder: stage.sortOrder,
          items: stage.items.map((item) => ({
            id: item.id,
            ref: item.id,
            predecessors: (item.predecessors ?? []).map((p) => ({
              ref: p.predecessorId,
              shareContent: p.shareContent,
            })),
            title: item.title,
            description: item.description ?? "",
            sortOrder: item.sortOrder,
            isRequired: item.isRequired,
            assigneeRole: item.assigneeRole as HiringProcessAssigneeRole,
            itemType:
              (item.itemType as HiringProcessItemType) ??
              HiringProcessItemType.Task,
            formId: item.formId ?? null,
            richTextContent: item.richTextContent ?? null,
            fileUploadIds: item.fileAttachments.map((fa) => fa.fileUpload.id),
            links: (item.links ?? []).map((link) => ({
              id: link.id,
              url: link.url,
              label: link.label ?? null,
              sortOrder: link.sortOrder,
            })),
            requestedCredentialFileTypeId:
              (item as { requestedCredentialFileTypeId?: number | null })
                .requestedCredentialFileTypeId ?? null,
          })),
        })),
      }
    : undefined;

  /**
   * Build a map of existing attachments keyed by `${stageIndex}-${itemIndex}`
   * so the form can display already-uploaded files as read-only rows.
   */
  const existingAttachmentsMap: ExistingAttachmentsMap | undefined =
    editingTemplateDetails
      ? (() => {
          const map: ExistingAttachmentsMap = new Map();
          editingTemplateDetails.stages.forEach((stage, stageIdx) => {
            stage.items.forEach((item, itemIdx) => {
              if (item.fileAttachments.length > 0) {
                map.set(
                  `${stageIdx}-${itemIdx}`,
                  item.fileAttachments.map((fa) => ({
                    fileUploadId: fa.fileUpload.id,
                    fileName: fa.fileUpload.fileName,
                    mimeType: fa.fileUpload.mimeType,
                    fileSizeBytes: fa.fileUpload.fileSizeBytes,
                    scanStatus: fa.fileUpload.scanStatus,
                  })),
                );
              }
            });
          });
          return map;
        })()
      : undefined;

  return (
    <div className="p-4 sm:p-6 w-full max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Layers className="h-6 w-6 text-blue-600" />
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              Hiring Process Templates
            </h1>
          </div>
          <p className="text-gray-500 text-sm">
            Create and manage hiring process templates for job postings.
            Templates define the stages and tasks candidates progress through.
          </p>
        </div>
        <Button
          onClick={() => setIsCreateOpen(true)}
          className="w-full sm:w-auto"
        >
          <Plus size={16} className="mr-1.5" />
          New Template
        </Button>
      </div>

      {/* Templates List */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin mb-3" />
          <p className="text-gray-500 text-sm">Loading templates...</p>
        </div>
      ) : !templates || templates.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-xl border border-dashed border-gray-200">
          <CheckSquare className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <h3 className="text-base font-medium text-gray-700 mb-1">
            No hiring process templates yet
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Create a template to define the stages providers move through during
            onboarding.
          </p>
          <Button variant="outline" onClick={() => setIsCreateOpen(true)}>
            <Plus size={14} className="mr-1.5" />
            Create First Template
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map((template) => (
            <TemplateRow
              key={template.id}
              template={template}
              organizationBusinessUnitId={buId}
              onEdit={handleEditClick}
              onDelete={setDeletingTemplate}
            />
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-4xl w-[calc(100vw-2rem)] sm:max-h-[90vh] overflow-y-auto !p-0">
          <DialogHeader className="mb-4 pt-6 px-4 sm:px-6">
            <DialogTitle>Create Hiring Process Template</DialogTitle>
          </DialogHeader>
          <HiringProcessTemplateForm
            onSubmit={handleCreate}
            onCancel={() => setIsCreateOpen(false)}
            isSubmitting={createMutation.isPending}
            error={createMutation.error?.message}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={!!editingTemplate}
        onOpenChange={(open) => {
          if (!open) {
            setEditingTemplate(null);
            setEditingTemplateDetails(null);
          }
        }}
      >
        <DialogContent className="max-w-4xl w-[calc(100vw-2rem)] sm:max-h-[90vh] overflow-y-auto !p-0">
          <DialogHeader className="mb-4 pt-6 px-4 sm:px-6">
            <DialogTitle>Edit Hiring Process Template</DialogTitle>
          </DialogHeader>
          {editingTemplateDetails ? (
            <HiringProcessTemplateForm
              defaultValues={editDefaultValues}
              existingAttachments={existingAttachmentsMap}
              onSubmit={handleUpdate}
              onCancel={() => {
                setEditingTemplate(null);
                setEditingTemplateDetails(null);
              }}
              isSubmitting={updateMutation.isPending}
              error={updateMutation.error?.message}
            />
          ) : (
            <div className="py-8 text-center text-gray-500">
              Loading template...
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <TemplateDeleteDialog
        template={deletingTemplate}
        organizationBusinessUnitId={buId}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingTemplate(null);
          }
        }}
        onConfirm={handleDelete}
      />
    </div>
  );
}
